import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { resolveWHSToken, getWorkerStatus, checkIn, checkOut } from "services/whs/whsService";
import { fetchUserDetailsByPhoneNumber, upsertUserWithCertification } from "services/userService";
import { uploadAttachments, DocumentUploadFeature } from "services/documentUploadService";
import signalRNotificationService from "services/signalrNotificationService";
import axiosInstance from "utils/axios";
import { setSession } from "utils/jwt";
import { getUserIdFromToken } from "utils/tokenUtils";
import { 
    LockClosedIcon, 
    BuildingOffice2Icon, 
    CheckCircleIcon, 
    ExclamationTriangleIcon, 
    MapPinIcon 
} from "@heroicons/react/24/outline";
import { useAuthContext } from "app/contexts/auth/context";
import { toast } from "sonner";
import { CheckOutConfirmationModal } from "../whs/components/admin/CheckOutConfirmationModal";
import {
    MAIN_HQ_CONFIG,
    isMainSite,
    evaluateGeofence,
    getJobGeofenceConfig
} from "../whs/utils/whsHQGeofence";

import Step1UserDetail from "./whs-qr/Step1UserDetail";
import Step3Certifications from "./whs-qr/Step3Certifications";
import { isSunnyHomes } from "./whs-qr/InputFormatters";

// Shared WHS Modular Primitives
import { getCoordinates, parseDate } from "./whs/shared/whsHelpers";
import WhsPageLayout from "./whs/shared/WhsPageLayout";
import WhsJobBanner from "./whs/shared/WhsJobBanner";
import LiveElapsedTimer from "./whs/attendance/LiveElapsedTimer";
import ActiveSessionCard from "./whs/attendance/ActiveSessionCard";
import CheckOutSuccessScreen from "./whs/attendance/CheckOutSuccessScreen";
import StaffCredentialsForm from "./whs/attendance/StaffCredentialsForm";
import QrCameraScannerModal from "./whs/attendance/QrCameraScannerModal";

let lastResolvedToken = null;

// Multi-Job Session Helpers: Allows Main Site (HQ) + 1 Regular Work Job to coexist simultaneously
export const getActiveSessions = () => {
    try {
        const raw = localStorage.getItem("whs_active_sessions");
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

export const getJobSession = (targetToken) => {
    if (!targetToken) return null;
    try {
        const raw = localStorage.getItem(`whs_session_${targetToken}`);
        if (raw) return JSON.parse(raw);
    } catch {
        // ignore
    }
    const sessions = getActiveSessions();
    for (const s of Object.values(sessions)) {
        if (s?.token === targetToken) return s;
    }
    return null;
};

export const saveJobSession = (targetToken, targetJobData, checkInDate, userId) => {
    if (!targetToken) return;
    const isMain = isMainSite(targetJobData);
    const slot = isMain ? "main" : "work";
    const jId = (targetJobData?.jobId || targetJobData?.JobId || "").toString();
    const dateStr = checkInDate instanceof Date ? checkInDate.toISOString() : String(checkInDate);

    const sessionObj = {
        token: targetToken,
        jobId: jId,
        isMainSite: isMain,
        checkInDate: dateStr,
        userId: userId || null,
    };

    localStorage.setItem(`whs_session_${targetToken}`, JSON.stringify(sessionObj));
    if (targetJobData) {
        localStorage.setItem(`whs_job_${targetToken}`, JSON.stringify(targetJobData));
    }

    try {
        const sessions = getActiveSessions();

        // If checking into a different work site, cascade-checkout previous work site
        if (slot === "work" && sessions.work && sessions.work.token !== targetToken) {
            const oldToken = sessions.work.token;
            const oldJobId = sessions.work.jobId;
            localStorage.removeItem(`whs_session_${oldToken}`);
            localStorage.removeItem(`whs_job_${oldToken}`);
            try {
                const ch = new BroadcastChannel("whs_tab_sync");
                ch.postMessage({
                    type: "CASCADE_WORK_CHECKOUT",
                    previousToken: oldToken,
                    previousJobId: oldJobId,
                    newJobId: jId,
                });
                ch.close();
            } catch (e) {
                // ignore
            }
        }

        sessions[slot] = sessionObj;
        localStorage.setItem("whs_active_sessions", JSON.stringify(sessions));
    } catch (e) {
        console.warn("Error saving active session:", e);
    }

    // Maintain backwards-compatible indicators
    localStorage.setItem("whs_is_checked_in", "true");
    localStorage.setItem("whs_active_token", targetToken);
    localStorage.setItem("whs_active_checkin_date", dateStr);
    localStorage.setItem("whs_sessions_sync", Date.now().toString());
};

export const clearJobSession = (targetToken, targetJobData) => {
    if (!targetToken && !targetJobData) return;
    const isMain = isMainSite(targetJobData);
    const slot = isMain ? "main" : "work";
    let changed = false;

    if (targetToken) {
        if (localStorage.getItem(`whs_session_${targetToken}`)) {
            localStorage.removeItem(`whs_session_${targetToken}`);
            changed = true;
        }
        localStorage.removeItem(`whs_job_${targetToken}`);
    }

    try {
        const sessions = getActiveSessions();
        if (sessions[slot]?.token === targetToken || (sessions[slot]?.jobId && targetJobData?.jobId && sessions[slot].jobId.toString().toLowerCase() === targetJobData.jobId.toString().toLowerCase())) {
            delete sessions[slot];
            localStorage.setItem("whs_active_sessions", JSON.stringify(sessions));
            changed = true;
        }

        const remaining = sessions.main || sessions.work;
        if (!remaining) {
            if (localStorage.getItem("whs_is_checked_in")) changed = true;
            localStorage.removeItem("whs_is_checked_in");
            localStorage.removeItem("whs_active_checkin_date");
            localStorage.removeItem("whs_active_token");
        } else {
            localStorage.setItem("whs_is_checked_in", "true");
            localStorage.setItem("whs_active_token", remaining.token || "");
            localStorage.setItem("whs_active_checkin_date", remaining.checkInDate || "");
        }
    } catch (e) {
        console.warn("Error clearing job session:", e);
    }

    if (changed) {
        localStorage.setItem("whs_sessions_sync", Date.now().toString());
    }
};

export default function WhsQrPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, logout, user } = useAuthContext();

    const [jobData, setJobData] = useState(() => {
        if (location.state?.jobData) return location.state.jobData;
        try {
            if (token) {
                const tokenJob = localStorage.getItem(`whs_job_${token}`);
                if (tokenJob) return JSON.parse(tokenJob);
            }
            const cached = localStorage.getItem("whs_job_data");
            if (cached) {
                const parsed = JSON.parse(cached);
                if (!token || parsed?.token === token || parsed?.Token === token) {
                    return parsed;
                }
            }
        } catch (e) {
            console.error("Failed to parse cached whs_job_data:", e);
        }
        return null;
    });

    const [error, setError] = useState(null);
    const [resolving, setResolving] = useState(() => {
        if (location.state?.jobData) return false;
        try {
            if (token && localStorage.getItem(`whs_job_${token}`)) return false;
            const cached = localStorage.getItem("whs_job_data");
            if (cached) {
                const parsed = JSON.parse(cached);
                if (!token || parsed?.token === token || parsed?.Token === token) {
                    return false;
                }
            }
        } catch {
            // ignore
        }
        return true;
    });

    // Credentials login state (for internal staff)
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [loginError, setLoginError] = useState(null);

    // Mode: "wizard" (3-step) vs "credentials" (internal staff)
    const [checkInMode, setCheckInMode] = useState("wizard");
    const [wizardStep, setWizardStep] = useState(1);

    const isHqJob = useMemo(() => isMainSite(jobData), [jobData]);

    const isHqSessionActive = useMemo(() => {
        try {
            return Boolean(localStorage.getItem("whs_hq_session"));
        } catch {
            return false;
        }
    }, []);

    // Location check status from resolveWHSToken API & site proximity comparison
    const [locationAccessStatus, setLocationAccessStatus] = useState(null);

    // 3-Step Wizard Form States
    const [phoneNumber, setPhoneNumber] = useState("");
    const [isExistingUser, setIsExistingUser] = useState(null);
    const [loadingLookup, setLoadingLookup] = useState(false);
    const [lookupError, setLookupError] = useState(null);

    const [userForm, setUserForm] = useState(() => {
        const storedIsInternal = localStorage.getItem("whs_is_internal_user");
        const isInternalVal =
            location.state?.isInternalUser !== undefined
                ? Boolean(location.state.isInternalUser)
                : storedIsInternal === "true"
                    ? true
                    : storedIsInternal === "false"
                        ? false
                        : false;
        return {
            userId: location.state?.userId || localStorage.getItem("whs_user_id") || null,
            fullName: location.state?.userForm?.fullName || "",
            phoneNumber: "",
            email: location.state?.userForm?.email || "",
            externalCompanyId: null,
            isInternalUser: isInternalVal,
        };
    });

    const [companyType, setCompanyType] = useState("other");
    const [companyForm, setCompanyForm] = useState({
        companyName: "",
        companyCode: "",
        abn: "",
        phoneNumber: "",
        email: "",
    });

    const [certificationsForm, setCertificationsForm] = useState({
        hasWhiteCard: false,
        hasSilicaCard: false,
        hasAsbestosCard: false,
        whiteCard: { cardNo: "", expiryDate: "", file: null },
        silica: { cardNo: "", expiryDate: "", file: null },
        asbestos: { cardNo: "", expiryDate: "", file: null },
    });

    const resetToScreen1 = useCallback(() => {
        setIsExistingUser(null);
        setPhoneNumber("");
        setWizardStep(1);
        setCheckInMode("wizard");
        setLookupError(null);
        setCompanyType("other");
        setUserForm({
            userId: null,
            fullName: "",
            phoneNumber: "",
            email: "",
            externalCompanyId: null,
            isInternalUser: false,
        });
        setCompanyForm({
            companyName: "",
            companyCode: "",
            abn: "",
            phoneNumber: "",
            email: "",
        });
        setCertificationsForm({
            hasWhiteCard: false,
            hasSilicaCard: false,
            hasAsbestosCard: false,
            whiteCard: { cardNo: "", expiryDate: "", file: null },
            silica: { cardNo: "", expiryDate: "", file: null },
            asbestos: { cardNo: "", expiryDate: "", file: null },
        });
    }, []);

    const [isSubmittingWizard, setIsSubmittingWizard] = useState(false);

    const [workerStatus, setWorkerStatus] = useState(() => location.state?.workerStatus || null);
    const [checkingIn, setCheckingIn] = useState(false);
    const [checkInSuccess, setCheckInSuccess] = useState(false);

    const [checkingOut, setCheckingOut] = useState(false);
    const [checkOutSuccess, setCheckOutSuccess] = useState(false);

    const [activeCheckInDate, setActiveCheckInDate] = useState(() => {
        if (location.state?.activeCheckInDate) {
            const parsed = new Date(location.state.activeCheckInDate);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        try {
            const mySession = getJobSession(token);
            if (mySession?.checkInDate) {
                const parsed = new Date(mySession.checkInDate);
                if (!isNaN(parsed.getTime())) return parsed;
            }
            // Fallback for single session or direct navigation
            const isCheckedIn = localStorage.getItem("whs_is_checked_in") === "true";
            const cachedDate = localStorage.getItem("whs_active_checkin_date");
            const cachedToken = localStorage.getItem("whs_active_token");
            if (isCheckedIn && cachedDate && (!token || !cachedToken || cachedToken === token)) {
                const parsed = new Date(cachedDate);
                if (!isNaN(parsed.getTime())) return parsed;
            }
        } catch (e) {
            console.error("Failed to parse cached active checkin date:", e);
        }
        return null;
    });

    const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
    const [finalCheckOutDate, setFinalCheckOutDate] = useState(null);

    useEffect(() => {
        if (activeCheckInDate && !checkOutSuccess && token) {
            saveJobSession(token, jobData, activeCheckInDate, userForm?.userId);
        }
    }, [activeCheckInDate, checkOutSuccess, token, jobData, userForm?.userId]);

    // Cross-tab synchronization
    useEffect(() => {
        const handleSync = (e) => {
            if (
                e.key === "whs_sessions_sync" ||
                e.key === `whs_session_${token}` ||
                e.key === "whs_logout_event"
            ) {
                const mySession = getJobSession(token);
                if (!mySession) {
                    setActiveCheckInDate((prev) => {
                        if (prev) {
                            setWorkerStatus((w) => (w ? { ...w, isCheckedIn: false } : null));
                            setCheckInSuccess(false);
                            resetToScreen1();
                        }
                        return null;
                    });
                } else if (mySession.checkInDate) {
                    const parsed = new Date(mySession.checkInDate);
                    if (!isNaN(parsed.getTime())) {
                        setActiveCheckInDate(parsed);
                    }
                }
            }
        };

        window.addEventListener("storage", handleSync);

        let channel = null;
        try {
            channel = new BroadcastChannel("whs_tab_sync");
            channel.onmessage = (msg) => {
                const data = msg?.data;
                if (!data) return;

                const currentJobId = (jobData?.jobId || jobData?.JobId || "").toString().toLowerCase();

                // Checkout targeted at THIS specific job or token
                if (data.type === "CHECKOUT") {
                    const matchesToken = data.token && token && data.token === token;
                    const matchesJob = data.jobId && currentJobId && data.jobId.toString().toLowerCase() === currentJobId;
                    if (matchesToken || matchesJob) {
                        setActiveCheckInDate((prev) => {
                            if (prev) {
                                setWorkerStatus((w) => (w ? { ...w, isCheckedIn: false } : null));
                                setCheckInSuccess(false);
                                resetToScreen1();
                            }
                            return null;
                        });
                    }
                }

                // If user checked into another work site in another tab, cascade checkout on previous work site
                if (data.type === "CASCADE_WORK_CHECKOUT") {
                    if (!isHqJob) {
                        const matchesPrevToken = data.previousToken && token && data.previousToken === token;
                        const matchesPrevJob = data.previousJobId && currentJobId && data.previousJobId.toString().toLowerCase() === currentJobId;
                        if (matchesPrevToken || matchesPrevJob) {
                            setActiveCheckInDate((prev) => {
                                if (prev) {
                                    toast.info("You've been signed out — you checked in at a different job site.");
                                    setWorkerStatus((w) => (w ? { ...w, isCheckedIn: false } : null));
                                    setCheckInSuccess(false);
                                    setCheckOutSuccess(false);
                                    resetToScreen1();
                                }
                                return null;
                            });
                        }
                    }
                }
            };
        } catch (err) {
            console.warn("BroadcastChannel error:", err);
        }

        return () => {
            window.removeEventListener("storage", handleSync);
            if (channel) channel.close();
        };
    }, [token, jobData, isHqJob, resetToScreen1]);

    // Camera scanning state
    const [isScanning, setIsScanning] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const isInternalUserSession = useMemo(() => {
        // Priority 1: If explicitly identified or checked in as external, NEVER show sidebar
        if (
            userForm?.isInternalUser === false ||
            localStorage.getItem("whs_is_internal_user") === "false" ||
            (companyType === "other" && companyForm?.companyName && !isSunnyHomes(companyForm.companyName))
        ) {
            return false;
        }

        // Priority 2: Explicit internal indicators
        if (
            userForm?.isInternalUser === true ||
            localStorage.getItem("whs_is_internal_user") === "true" ||
            location.state?.isInternalUser === true
        ) {
            return true;
        }

        // Credentials login via "Internal Staff Sign In"
        if (localStorage.getItem("whs_username") && localStorage.getItem("authToken")) {
            return true;
        }

        // Priority 3: Only check active CRM user if not explicitly external
        if (
            user &&
            (user.isInternalUser || user.IsInternalUser || user.isInternalRole || (!user.externalCompanyId && isSunnyHomes(user.companyName)))
        ) {
            return true;
        }

        return false;
    }, [userForm?.isInternalUser, companyType, companyForm?.companyName, user, location.state]);

    // Ensure jsQR is loaded
    useEffect(() => {
        if (!window.jsQR) {
            const script = document.createElement("script");
            script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const startCamera = async () => {
        try {
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
            } catch (e) {
                stream = await navigator.mediaDevices.getUserMedia({ video: true });
            }
            setCameraStream(stream);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.warn("Camera access denied or not supported:", err);
            toast.error("Camera access is required to scan QR code");
            setIsScanning(false);
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
            setCameraStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    useEffect(() => {
        if (videoRef.current && cameraStream) {
            videoRef.current.srcObject = cameraStream;
        }
    }, [cameraStream]);

    const handleDecodedQR = (scannedContent) => {
        if (!scannedContent) return;
        setIsScanning(false);
        stopCamera();

        const currentJobId = (jobData?.jobId || jobData?.JobId || "").toLowerCase();
        let scannedJobId = null;
        let scannedToken = null;

        try {
            if (scannedContent.includes("/whs/")) {
                const parts = scannedContent.split("/whs/");
                if (parts[1]) {
                    scannedToken = parts[1].split("?")[0].split("/")[0].trim();
                }
            } else if (/^[a-f0-9]{32}$/i.test(scannedContent.trim())) {
                scannedToken = scannedContent.trim();
            }

            if (scannedContent.includes("jobId=")) {
                const urlObj = new URL(scannedContent);
                scannedJobId = urlObj.searchParams.get("jobId");
            }
        } catch (e) {
            console.warn("Error parsing QR content:", e);
        }

        const isTokenMatch = scannedToken && token && scannedToken.toLowerCase() === token.toLowerCase();
        const isJobMatch = scannedJobId && currentJobId && scannedJobId.toLowerCase() === currentJobId;

        if (isTokenMatch || isJobMatch || (!scannedJobId && !scannedToken && scannedContent.toLowerCase().includes(currentJobId))) {
            setIsCheckOutModalOpen(true);
        } else if (scannedToken) {
            toast.info("Validating scanned job site QR...");
            resolveWHSToken(scannedToken)
                .then((res) => {
                    const resolved = res?.data || res;
                    const resJobId = (resolved?.jobId || resolved?.JobId || "").toLowerCase();
                    if (resJobId && resJobId === currentJobId) {
                        setIsCheckOutModalOpen(true);
                    } else {
                        toast.error("This QR code is for a different job site.");
                    }
                })
                .catch(() => {
                    toast.error("Could not verify the scanned QR code.");
                });
        } else {
            toast.error("QR Code does not match this job site. Please align the correct QR code.");
        }
    };

    useEffect(() => {
        let animationFrameId;
        let checkStream;

        const scanFrame = () => {
            if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                const video = videoRef.current;
                if (!canvasRef.current) {
                    canvasRef.current = document.createElement("canvas");
                }
                const canvas = canvasRef.current;
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = window.jsQR
                    ? window.jsQR(imageData.data, imageData.width, imageData.height, {
                        inversionAttempts: "dontInvert",
                    })
                    : null;

                if (code && code.data) {
                    handleDecodedQR(code.data);
                    return;
                }
            }
            animationFrameId = requestAnimationFrame(scanFrame);
        };

        if (isScanning) {
            startCamera();
            checkStream = setInterval(() => {
                if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                    clearInterval(checkStream);
                    scanFrame();
                }
            }, 100);

            return () => {
                clearInterval(checkStream);
                cancelAnimationFrame(animationFrameId);
                stopCamera();
            };
        } else {
            stopCamera();
        }
    }, [isScanning]);

    // Initial Token Resolution
    useEffect(() => {
        if (!token) {
            setError("No token provided");
            setResolving(false);
            return;
        }

        const isFromMenuNavigation =
            Boolean(location.state?.fromMenu) ||
            Boolean(location.state?.jobData && (location.state?.activeCheckInDate || location.state?.workerStatus));

        if ((isFromMenuNavigation || lastResolvedToken === token) && (jobData || location.state?.jobData)) {
            setResolving(false);
            return;
        }

        resolve();
    }, [token, location.state?.fromMenu]);

    const resolve = async () => {
        try {
            if (!jobData && !location.state?.jobData && !localStorage.getItem("whs_job_data")) {
                setResolving(true);
            }

            // 1. Immediately request browser location coordinates upon scan/visit
            let userCoords = null;
            try {
                userCoords = await getCoordinates();
            } catch (geoErr) {
                console.warn("Location prompt error during initial scan:", geoErr);
            }

            const hasValidCoords = Boolean(userCoords && (userCoords.latitude || userCoords.longitude));
            const resolveParams = hasValidCoords
                ? {
                      latitude: userCoords.latitude,
                      longitude: userCoords.longitude,
                      accuracy: userCoords.accuracy || 0,
                  }
                : {};

            // 2. Resolve token passing location parameters to backend
            const result = await resolveWHSToken(token, resolveParams);

            if (!result || result.status === "Error" || result.statusCode >= 400) {
                setError(result?.message || "Invalid or expired QR code");
                return;
            }

            const resolvedData = result.data || result;
            if (resolvedData.isWHSActive === false || resolvedData.isActive === false) {
                setError("This job site is currently deactivated or closed for WHS check-in.");
                return;
            }
            lastResolvedToken = token;
            setJobData(resolvedData);
            localStorage.setItem("whs_job_data", JSON.stringify(resolvedData));
            if (token) {
                localStorage.setItem(`whs_job_${token}`, JSON.stringify(resolvedData));
            }

            // 3. Determine if user can check in to this job site
            // Backend will compare job site location and user location:
            // Supports: canCheckIn, allowCheckIn, isWithinLocation, isEligible
            let canCheckIn = null;
            let blockedReason = null;

            // WHS_ResolveToken now computes this server-side (authoritative — same Haversine/geography
            // check WHS_CheckIn re-validates at commit time) using the userCoords sent above against
            // the site's real Latitude/Longitude/GeofenceRadiusMeters. isWithinGeofence is a nullable
            // bool: null means "unknown" (no coords supplied, or this site has no coordinates
            // configured), never "outside" — only fall back to the client-side estimate in that case.
            if (resolvedData.isWithinGeofence !== undefined && resolvedData.isWithinGeofence !== null) {
                canCheckIn = Boolean(resolvedData.isWithinGeofence);
                const distance = resolvedData.distanceMeters;
                const radius = resolvedData.geofenceRadiusMeters || resolvedData.GeofenceRadiusMeters || 50;
                blockedReason = canCheckIn
                    ? null
                    : `Check-in Blocked: You are ${distance != null ? Math.round(distance) : "too far"}m away — you must be within ${radius}m of the site to check in.`;
            } else if (resolvedData.canCheckIn !== undefined) {
                canCheckIn = Boolean(resolvedData.canCheckIn);
                blockedReason = resolvedData.blockedReason || resolvedData.message;
            } else if (resolvedData.allowCheckIn !== undefined) {
                canCheckIn = Boolean(resolvedData.allowCheckIn);
                blockedReason = resolvedData.blockedReason || resolvedData.message;
            } else if (resolvedData.isWithinLocation !== undefined) {
                canCheckIn = Boolean(resolvedData.isWithinLocation);
                blockedReason = resolvedData.blockedReason || resolvedData.message;
            } else if (!hasValidCoords) {
                canCheckIn = false;
                blockedReason = "Location access is required to verify your site presence. Please enable location permissions in your browser.";
            } else {
                // Backend returned isWithinGeofence=null -- this site has no coordinates configured
                // server-side, so there's nothing to enforce (shouldn't happen for the Main site,
                // which is backfilled, but can happen for a Work site without Latitude/Longitude set).
                canCheckIn = true;
            }

            setLocationAccessStatus({
                canCheckIn: canCheckIn !== false,
                reason: blockedReason || "You must be present at the site to check in.",
                isPermissionDenied: !hasValidCoords,
                coords: hasValidCoords ? userCoords : null,
                checked: true,
            });

            const savedToken = localStorage.getItem("authToken");
            const savedUserId = localStorage.getItem("whs_user_id");
            const savedPhoneNumber = localStorage.getItem("whs_phone_number");

            if (savedToken) {
                setSession(savedToken);
            }

            const activeUserId = getUserIdFromToken() || savedUserId || userForm?.userId || null;
            const jobId = resolvedData.jobId || resolvedData.JobId;
            const resolvedCompanyId = resolvedData.companyId || resolvedData.CompanyId || null;

            if (resolvedCompanyId) {
                localStorage.setItem("whs_company_id", resolvedCompanyId);
            }
            if (activeUserId) {
                localStorage.setItem("whs_user_id", activeUserId);
            }

            const isAutoCheckIn = Boolean(
                location.state?.autoCheckIn ||
                localStorage.getItem("whs_auto_checkin") === "true"
            );

            if (isAutoCheckIn) {
                localStorage.removeItem("whs_auto_checkin");
            }

            if (jobId) {
                try {
                    const statusResult = await getWorkerStatus(jobId, activeUserId, resolvedCompanyId);
                    const statusData = statusResult?.data || statusResult || {};
                    setWorkerStatus(statusData);

                    const statusUserId = statusData.userId || statusData.UserId;
                    if (statusUserId) {
                        localStorage.setItem("whs_user_id", statusUserId);
                        setUserForm((prev) => ({ ...prev, userId: statusUserId }));
                    }

                    const isCheckedIn = Boolean(statusData.isCheckedIn || statusData.IsCheckedIn);
                    if (isCheckedIn) {
                        const checkInTime = statusData.inTime || statusData.InTime || statusData.checkInTime || statusData.CheckInTime;
                        const parsedDate = parseDate(checkInTime) || new Date();
                        setActiveCheckInDate(parsedDate);
                        saveJobSession(token, resolvedData, parsedDate, statusUserId || activeUserId);
                        if (savedPhoneNumber) {
                            setPhoneNumber(savedPhoneNumber);
                        }
                        const storedInternal = localStorage.getItem("whs_is_internal_user");
                        if (storedInternal === "false" || userForm?.isInternalUser === false) {
                            localStorage.setItem("whs_is_internal_user", "false");
                            setUserForm((prev) => ({ ...prev, isInternalUser: false }));
                        } else {
                            const isInternalKnown =
                                location.state?.isInternalUser === true ||
                                storedInternal === "true" ||
                                Boolean(localStorage.getItem("whs_username") && localStorage.getItem("authToken"));

                            if (isInternalKnown) {
                                localStorage.setItem("whs_is_internal_user", "true");
                                setUserForm((prev) => ({ ...prev, isInternalUser: true }));
                            } else if (savedPhoneNumber || activeUserId) {
                                fetchUserDetailsByPhoneNumber(savedPhoneNumber || "", jobId)
                                    .then((phoneRes) => {
                                        if (phoneRes?.success && phoneRes?.data) {
                                            const parsed = typeof phoneRes.data === "string" ? JSON.parse(phoneRes.data) : phoneRes.data;
                                            const uData = parsed.user || parsed.User || parsed;
                                            const cData = parsed.company || parsed.Company;
                                            const compName = cData?.companyName || cData?.CompanyName || uData.companyName || uData.CompanyName || "";
                                            const checkIsInternal = Boolean(
                                                uData.isInternalUser ??
                                                uData.IsInternalUser ??
                                                uData.isInternalRole ??
                                                uData.IsInternalRole ??
                                                (!uData.externalCompanyId && !uData.ExternalCompanyId && isSunnyHomes(compName))
                                            );
                                            localStorage.setItem("whs_is_internal_user", checkIsInternal ? "true" : "false");
                                            setUserForm((prev) => ({ ...prev, isInternalUser: checkIsInternal }));
                                        }
                                    })
                                    .catch(console.error);
                            }
                        }
                    } else if (isAutoCheckIn && activeUserId) {
                        if (canCheckIn === false) {
                            toast.error(blockedReason || "Check-in Blocked: You must be present at the site to check in.");
                            return;
                        }
                        setCheckingIn(true);
                        try {
                            const coords = userCoords || (await getCoordinates());
                            const res = await checkIn({
                                jobId: jobId,
                                latitude: coords.latitude,
                                longitude: coords.longitude,
                                accuracy: coords.accuracy,
                                userId: activeUserId,
                                companyId: resolvedCompanyId,
                            });

                            if (!res || res.status === "Error" || res.statusCode >= 400 || res.isSuccess === false || res.success === false) {
                                const errorMsg = res?.message || res?.Message || res?.data?.message || "Check-in failed. Please verify site access.";
                                throw new Error(errorMsg);
                            }

                            toast.success("Checked in successfully!");
                            setCheckInSuccess(true);
                            localStorage.setItem("whs_is_internal_user", "true");
                            setUserForm((prev) => ({ ...prev, isInternalUser: true }));
                            const resData = res?.data || res;
                            const checkInTime = resData?.inTime || resData?.InTime || res?.inTime || res?.InTime;
                            setActiveCheckInDate(parseDate(checkInTime) || new Date());

                            const freshStatus = await getWorkerStatus(jobId, activeUserId, resolvedCompanyId);
                            setWorkerStatus(freshStatus?.data || freshStatus);
                        } catch (autoCheckInErr) {
                            console.error("Auto check-in error:", autoCheckInErr);
                            const errMsg = autoCheckInErr?.response?.data?.message || autoCheckInErr?.response?.data?.Message || autoCheckInErr?.message || "Failed to complete check-in";
                            const isAlreadySignedIn = typeof errMsg === "string" && (errMsg.toLowerCase().includes("already signed in") || errMsg.toLowerCase().includes("already checked in"));

                            if (isAlreadySignedIn) {
                                toast.info("You are already checked in for this job.");
                                try {
                                    const freshStatus = await getWorkerStatus(jobId, activeUserId, resolvedCompanyId);
                                    const freshData = freshStatus?.data || freshStatus || {};
                                    setWorkerStatus(freshData);
                                    const checkInTime = freshData.inTime || freshData.InTime || freshData.checkInTime || freshData.CheckInTime;
                                    setActiveCheckInDate(parseDate(checkInTime) || new Date());
                                    setCheckInSuccess(true);
                                    localStorage.setItem("whs_is_internal_user", "true");
                                    setUserForm((prev) => ({ ...prev, isInternalUser: true }));
                                } catch (e) {
                                    console.error("Error fetching worker status:", e);
                                }
                            } else {
                                toast.error(errMsg);
                            }
                        } finally {
                            setCheckingIn(false);
                        }
                    } else {
                        if (!location.state?.activeCheckInDate) {
                            setActiveCheckInDate(null);
                            setIsExistingUser(null);
                        }
                    }
                } catch (e) {
                    console.error("Status fetch failed on refresh:", e);
                }
            }
        } catch (err) {
            if (!jobData) {
                setError(err.response?.data?.message || err.message || "Error resolving QR code");
            }
        } finally {
            setResolving(false);
        }
    };

    const handleRetryLocation = async () => {
        try {
            if (typeof navigator !== "undefined" && navigator.permissions && navigator.permissions.query) {
                const perm = await navigator.permissions.query({ name: "geolocation" });
                if (perm.state === "denied") {
                    toast.error(
                        "Location is blocked in browser settings. Please click 'Reset to default' or toggle Location to ON in the site settings popup.",
                        { duration: 5000 }
                    );
                }
            }
        } catch (e) {}
        setResolving(true);
        await resolve();
    };

    // Phone Lookup API
    const handleLookupPhone = async (numToLookup) => {
        const rawNum = numToLookup || phoneNumber;
        if (!rawNum || rawNum.length < 8) {
            setLookupError("Please enter a valid mobile phone number.");
            return;
        }

        const currentJobId = jobData?.jobId || jobData?.JobId;
        if (!currentJobId) {
            toast.error("Job context missing. Please reload page.");
            return;
        }

        setLoadingLookup(true);
        setLookupError(null);

        try {
            const res = await fetchUserDetailsByPhoneNumber(rawNum, currentJobId);
            if (res.success && res.data) {
                const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
                setIsExistingUser(true);

                const u = data.user || data.User || data;
                const fetchedUserId = u.userId || u.UserId || null;
                if (fetchedUserId) {
                    localStorage.setItem("whs_user_id", fetchedUserId);
                }
                if (rawNum) {
                    localStorage.setItem("whs_phone_number", rawNum);
                }

                const c = data.company || data.Company;
                const cCompanyId = c?.companyId || c?.CompanyId || jobData?.companyId || jobData?.CompanyId;
                if (cCompanyId) {
                    localStorage.setItem("whs_company_id", cCompanyId);
                }
                const cName = c?.companyName || c?.CompanyName || u.companyName || u.CompanyName || "";

                const checkIsInternalUser = Boolean(
                    u.isInternalUser ??
                    u.IsInternalUser ??
                    u.isInternalRole ??
                    u.IsInternalRole ??
                    (!u.externalCompanyId && !u.ExternalCompanyId && isSunnyHomes(cName))
                );

                localStorage.setItem("whs_is_internal_user", checkIsInternalUser ? "true" : "false");

                setUserForm({
                    userId: fetchedUserId,
                    fullName: u.fullName || u.FullName || u.name || "",
                    phoneNumber: u.phoneNumber || u.PhoneNumber || rawNum,
                    email: u.email || u.Email || "",
                    externalCompanyId: u.externalCompanyId || u.ExternalCompanyId || null,
                    isInternalUser: checkIsInternalUser,
                });

                if (checkIsInternalUser || isSunnyHomes(cName)) {
                    setCompanyType("sunny_homes");
                    setCompanyForm({
                        companyName: cName || "Sunny Homes",
                        companyCode: "SH",
                        abn: "",
                        phoneNumber: "",
                        email: "",
                    });
                } else {
                    setCompanyType("other");
                    setCompanyForm({
                        companyName: cName,
                        companyCode: c?.companyCode || c?.CompanyCode || "",
                        abn: c?.abn || c?.ABN || "",
                        phoneNumber: c?.phoneNumber || c?.PhoneNumber || "",
                        email: c?.email || c?.Email || "",
                    });
                }

                const certs = data.certifications || data.Certifications;
                if (certs) {
                    const parseCertCard = (rawCard) => {
                        if (!rawCard) return { certificationDataId: null, cardNo: "", expiryDate: "", file: null, files: [] };
                        const rawFiles = rawCard.documents || rawCard.Documents || [];
                        const normalizedFiles = rawFiles.map((f) => {
                            const fileName = f.name || f.originalFilename || f.OriginalFilename || f.fileName || f.FileName || "document";
                            const fileSize = Number(f.size ?? f.fileSize ?? f.FileSize ?? 0);
                            return {
                                ...f,
                                name: fileName,
                                size: fileSize,
                                filePath: f.filePath || f.fileURL || f.FileURL || f.storedFilename || f.StoredFilename,
                                fileURL: f.fileURL || f.FileURL || f.storedFilename || f.StoredFilename,
                                storedFilename: f.storedFilename || f.StoredFilename,
                                originalFilename: fileName,
                                dataDocumentId: f.dataDocumentId || f.DataDocumentId || null,
                            };
                        });
                        return {
                            certificationDataId: rawCard.certificationDataId || rawCard.CertificationDataId || null,
                            certificationName: rawCard.certificationName || rawCard.CertificationName || "",
                            certificationCode: rawCard.certificationCode || rawCard.CertificationCode || "",
                            cardNo: rawCard.cardNo || rawCard.CardNo || "",
                            expiryDate: rawCard.expiryDate || rawCard.ExpiryDate || "",
                            file: normalizedFiles[0] || null,
                            files: normalizedFiles,
                        };
                    };

                    setCertificationsForm({
                        hasWhiteCard: Boolean(certs.hasWhiteCard ?? certs.HasWhiteCard),
                        hasSilicaCard: Boolean(certs.hasSilicaCard ?? certs.HasSilicaCard),
                        hasAsbestosCard: Boolean(certs.hasAsbestosCard ?? certs.HasAsbestosCard),
                        whiteCard: parseCertCard(certs.whiteCard || certs.WhiteCard),
                        silica: parseCertCard(certs.silica || certs.Silica),
                        asbestos: parseCertCard(certs.asbestos || certs.Asbestos),
                    });
                }
            } else {
                setIsExistingUser(false);
                localStorage.setItem("whs_is_internal_user", "false");
                setUserForm({
                    userId: null,
                    fullName: "",
                    phoneNumber: rawNum,
                    email: "",
                    externalCompanyId: null,
                    isInternalUser: false,
                });
                setCompanyType("other");
                setCompanyForm({
                    companyName: "",
                    companyCode: "",
                    abn: "",
                    phoneNumber: "",
                    email: "",
                });
                setCertificationsForm({
                    hasWhiteCard: false,
                    hasSilicaCard: false,
                    hasAsbestosCard: false,
                    whiteCard: { cardNo: "", expiryDate: "", file: null },
                    silica: { cardNo: "", expiryDate: "", file: null },
                    asbestos: { cardNo: "", expiryDate: "", file: null },
                });
            }
        } catch (err) {
            console.error("Phone lookup error:", err);
            setIsExistingUser(false);
            localStorage.setItem("whs_is_internal_user", "false");
            setUserForm({
                userId: null,
                fullName: "",
                phoneNumber: rawNum,
                email: "",
                externalCompanyId: null,
                isInternalUser: false,
            });
            setCompanyType("other");
            setCompanyForm({
                companyName: "",
                companyCode: "",
                abn: "",
                phoneNumber: "",
                email: "",
            });
            setCertificationsForm({
                hasWhiteCard: false,
                hasSilicaCard: false,
                hasAsbestosCard: false,
                whiteCard: { cardNo: "", expiryDate: "", file: null },
                silica: { cardNo: "", expiryDate: "", file: null },
                asbestos: { cardNo: "", expiryDate: "", file: null },
            });
        } finally {
            setLoadingLookup(false);
        }
    };

    // Wizard Submit: Upsert & Check In
    const handleWizardSubmit = async () => {
        const currentJobId = jobData?.jobId || jobData?.JobId;
        let upsertedUserId = userForm?.userId || null;
        let resolvedCompanyId = jobData?.companyId || jobData?.CompanyId || null;

        if (!currentJobId) {
            toast.error("Job context not found.");
            return;
        }

        try {
            setIsSubmittingWizard(true);

            const processDocs = async (cardData) => {
                if (!cardData) return [];
                const rawFiles = cardData.files || (cardData.file ? [cardData.file] : null) || cardData.documents || [];
                const items = (Array.isArray(rawFiles) ? rawFiles : [rawFiles]).filter(Boolean);

                const existingDocs = [];
                const newFilesToUpload = [];

                items.forEach((item) => {
                    if (item instanceof File || item instanceof Blob) {
                        newFilesToUpload.push(item);
                    } else if (item?.file && (item.file instanceof File || item.file instanceof Blob)) {
                        newFilesToUpload.push(item.file);
                    } else if (typeof item === 'object') {
                        const fileName = item.name || item.originalFilename || item.OriginalFilename || item.fileName || item.FileName || "document";
                        const filePath = item.filePath || item.storedFilename || item.StoredFilename || item.fileURL || item.FileURL || "";
                        existingDocs.push({
                            dataDocumentId: item.dataDocumentId || item.DataDocumentId || null,
                            originalFilename: fileName,
                            storedFilename: filePath,
                            fileURL: filePath,
                            fileSize: Number(item.fileSize ?? item.FileSize ?? item.size ?? 0),
                            uploadedAt: item.uploadedAt || item.UploadedAt || new Date().toISOString(),
                        });
                    }
                });

                let newlyUploadedDocs = [];
                if (newFilesToUpload.length > 0) {
                    const currentCompanyId = resolvedCompanyId || jobData?.companyId || jobData?.CompanyId || null;
                    const uploadRes = await uploadAttachments(
                        DocumentUploadFeature.USER_CERTIFICATIONS,
                        newFilesToUpload,
                        currentCompanyId
                    );

                    if (uploadRes.success && uploadRes.data && uploadRes.data.length > 0) {
                        newlyUploadedDocs = uploadRes.data.map((att, idx) => ({
                            dataDocumentId: null,
                            originalFilename: att.fileName || newFilesToUpload[idx]?.name || "document",
                            storedFilename: att.filePath,
                            fileURL: att.filePath,
                            fileSize: newFilesToUpload[idx]?.size || 0,
                            uploadedAt: new Date().toISOString(),
                        }));
                    }
                }

                return [...existingDocs, ...newlyUploadedDocs];
            };

            const whiteCardDocs = await processDocs(certificationsForm.whiteCard);
            const silicaDocs = await processDocs(certificationsForm.silica);
            const asbestosDocs = await processDocs(certificationsForm.asbestos);

            const isInternal = companyType === "sunny_homes" || isSunnyHomes(companyForm.companyName) || Boolean(userForm.isInternalUser);
            const payload = {
                jobId: currentJobId,
                company: {
                    companyName: isInternal ? "Sunny Homes" : companyForm.companyName || "",
                    companyCode: isInternal ? "SH" : companyForm.companyCode || "",
                    abn: isInternal ? "" : companyForm.abn || "",
                    phoneNumber: isInternal ? "" : companyForm.phoneNumber || "",
                    email: isInternal ? "" : companyForm.email || "",
                },
                user: {
                    userId: userForm.userId || null,
                    fullName: userForm.fullName || "",
                    phoneNumber: userForm.phoneNumber || phoneNumber || "",
                    email: userForm.email || "",
                    externalCompanyId: userForm.externalCompanyId || null,
                },
                certifications: {
                    hasWhiteCard: Boolean(certificationsForm.hasWhiteCard),
                    hasSilicaCard: Boolean(certificationsForm.hasSilicaCard),
                    hasAsbestosCard: Boolean(certificationsForm.hasAsbestosCard),
                    whiteCard: certificationsForm.hasWhiteCard
                        ? {
                            certificationDataId: certificationsForm.whiteCard?.certificationDataId || null,
                            certificationName: "General Construction Induction",
                            certificationCode: "WHITE_CARD",
                            cardNo: certificationsForm.whiteCard?.cardNo || "",
                            expiryDate: certificationsForm.whiteCard?.expiryDate
                                ? new Date(certificationsForm.whiteCard.expiryDate).toISOString()
                                : null,
                            documents: whiteCardDocs,
                        }
                        : null,
                    silica: certificationsForm.hasSilicaCard
                        ? {
                            certificationDataId: certificationsForm.silica?.certificationDataId || null,
                            certificationName: "Crystalline Silica Induction",
                            certificationCode: "SILICA",
                            cardNo: certificationsForm.silica?.cardNo || "",
                            expiryDate: certificationsForm.silica?.expiryDate
                                ? new Date(certificationsForm.silica.expiryDate).toISOString()
                                : null,
                            documents: silicaDocs,
                        }
                        : null,
                    asbestos: certificationsForm.hasAsbestosCard
                        ? {
                            certificationDataId: certificationsForm.asbestos?.certificationDataId || null,
                            certificationName: "Asbestos Awareness",
                            certificationCode: "ASBESTOS",
                            cardNo: certificationsForm.asbestos?.cardNo || "",
                            expiryDate: certificationsForm.asbestos?.expiryDate
                                ? new Date(certificationsForm.asbestos.expiryDate).toISOString()
                                : null,
                            documents: asbestosDocs,
                        }
                        : null,
                },
            };

            const upsertRes = await upsertUserWithCertification(payload);
            if (!upsertRes.success) {
                throw new Error(upsertRes.message || "Failed to save worker & certification details.");
            }

            const upsertedDataRaw = upsertRes.data || {};
            try {
                const parsedData = typeof upsertedDataRaw === "string" ? JSON.parse(upsertedDataRaw) : upsertedDataRaw;
                const u = parsedData?.user || parsedData?.User || parsedData;
                if (u?.userId || u?.UserId) {
                    upsertedUserId = u.userId || u.UserId;
                }
                if (parsedData?.token) {
                    setSession(parsedData.token);
                }
            } catch (e) {
                console.error("Failed to parse upsert response data:", e);
            }

            localStorage.setItem("whs_is_internal_user", isInternal ? "true" : "false");
            setUserForm((prev) => ({
                ...prev,
                userId: upsertedUserId || prev.userId,
                isInternalUser: isInternal,
            }));
            if (upsertedUserId) {
                localStorage.setItem("whs_user_id", upsertedUserId);
            }
            if (resolvedCompanyId) {
                localStorage.setItem("whs_company_id", resolvedCompanyId);
            }
            if (phoneNumber) {
                localStorage.setItem("whs_phone_number", phoneNumber);
            }
            if (locationAccessStatus && locationAccessStatus.canCheckIn === false) {
                toast.error(locationAccessStatus.reason || "Check-in Blocked: You must be present at the site to check in.");
                return;
            }

            setCheckingIn(true);
            let coords = locationAccessStatus?.coords;
            if (!coords || (!coords.latitude && !coords.longitude)) {
                try {
                    coords = await getCoordinates();
                } catch (geoErr) {
                    console.warn("Location permission error:", geoErr);
                    toast.error("Location permission denied. Please allow location in your browser.");
                    setCheckingIn(false);
                    return;
                }
            }

            // Fail-fast UX check before the network round-trip -- WHS_CheckIn re-validates this
            // authoritatively server-side regardless, so this is just to avoid a wasted request.
            // Uses the site's real coordinates from jobData (set via WHS_ResolveToken), for Main
            // and Work sites alike -- no more hardcoded MAIN_HQ_CONFIG special-casing.
            let hqDistance = "On Site";
            const siteConfig = getJobGeofenceConfig(jobData);
            if (siteConfig && coords) {
                const geoResult = evaluateGeofence(coords, siteConfig);
                if (!geoResult.isWithinGeofence) {
                    toast.error(
                        isHqJob
                            ? "Check-in Blocked: You must be present at the site to check in."
                            : "Check-in Blocked: You must be present at the job site to check in."
                    );
                    setCheckingIn(false);
                    return;
                }
            }

            const res = await checkIn({
                jobId: currentJobId,
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracy: coords.accuracy,
                userId: upsertedUserId || null,
                companyId: resolvedCompanyId,
            });

            if (!res || res.status === "Error" || res.statusCode >= 400 || res.isSuccess === false || res.success === false) {
                const errorMsg = res?.message || res?.Message || res?.data?.message || "Check-in failed. Please verify site access.";
                throw new Error(errorMsg);
            }

            toast.success("Checked in successfully!");
            setCheckInSuccess(true);
            const resData = res?.data || res;
            const checkInTime = resData?.inTime || resData?.InTime || res?.inTime || res?.InTime;
            setActiveCheckInDate(parseDate(checkInTime) || new Date());

            if (isHqJob) {
                const hqSess = {
                    sessionId: `hq-${Date.now()}`,
                    JobId: MAIN_HQ_CONFIG.id,
                    JobName: MAIN_HQ_CONFIG.name,
                    isMainSite: true,
                    inTime: checkInTime ? new Date(checkInTime).toISOString() : new Date().toISOString(),
                    outTime: null,
                    isCheckedIn: true,
                    distance: hqDistance,
                };
                localStorage.setItem("whs_hq_session", JSON.stringify(hqSess));
            }

            try {
                const freshStatus = await getWorkerStatus(currentJobId, upsertedUserId || userForm?.userId || null, resolvedCompanyId || null);
                setWorkerStatus(freshStatus?.data || freshStatus);
            } catch (e) {
                console.error("Fresh status fetch error:", e);
            }
        } catch (err) {
            console.error("Wizard check-in error:", err);
            const errMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.message || "Failed to complete check-in";

            const isAlreadySignedIn =
                typeof errMsg === "string" &&
                (errMsg.toLowerCase().includes("already signed in") ||
                    errMsg.toLowerCase().includes("already checked in"));

            if (isAlreadySignedIn && currentJobId) {
                toast.info("You are already checked in for this job.");
                try {
                    const freshStatus = await getWorkerStatus(
                        currentJobId,
                        upsertedUserId || localStorage.getItem("whs_user_id") || userForm?.userId || null,
                        resolvedCompanyId
                    );
                    const statusData = freshStatus?.data || freshStatus || {};
                    setWorkerStatus(statusData);

                    const checkInTime =
                        statusData.inTime ||
                        statusData.InTime ||
                        statusData.checkInTime ||
                        statusData.CheckInTime;
                    setActiveCheckInDate(parseDate(checkInTime) || new Date());
                    setCheckInSuccess(true);
                } catch (statusErr) {
                    console.error("Error fetching worker status after already signed in:", statusErr);
                }
            } else {
                toast.error(errMsg);
            }
        } finally {
            setIsSubmittingWizard(false);
            setCheckingIn(false);
        }
    };

    // Credentials Login Handler (Staff)
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password) {
            setLoginError("Please enter both username and password");
            return;
        }

        try {
            setLoggingIn(true);
            setLoginError(null);

            const verifyResponse = await axiosInstance.get(
                `/api/Account/CheckUserVerificationOrSendOtp?userName=${encodeURIComponent(username)}`
            );

            const verifyResult = verifyResponse.data;
            if (!verifyResult || verifyResult.status !== "Success") {
                setLoginError(verifyResult?.message || "User verification check failed");
                return;
            }

            const loginResponse = await axiosInstance.get(
                `/api/Account/UserLogin?userName=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`
            );

            const loginResult = loginResponse.data;
            if (!loginResult || loginResult.status !== "Success") {
                setLoginError(loginResult?.message || "Login failed");
                return;
            }

            let userData;
            try {
                userData = typeof loginResult.data === "string" ? JSON.parse(loginResult.data) : loginResult.data;
            } catch (e) {
                console.error("Failed to parse login data:", e);
                setLoginError("Invalid login response format");
                return;
            }

            if (!userData?.token) {
                setLoginError("No auth token received from server");
                return;
            }

            setSession(userData.token);

            const jobId = jobData.jobId || jobData.JobId;
            if (!jobId) {
                setLoginError("Job ID not found in resolved QR data");
                setSession(null);
                return;
            }

            const activeUserId = userData.userId || userData.UserId;
            const resolvedCompanyId = jobData?.companyId || jobData?.CompanyId || userData?.companyId || userData?.CompanyId || null;

            if (activeUserId) {
                localStorage.setItem("whs_user_id", activeUserId);
            }
            if (resolvedCompanyId) {
                localStorage.setItem("whs_company_id", resolvedCompanyId);
            }

            const statusResult = await getWorkerStatus(jobId, activeUserId || null, resolvedCompanyId || null);
            if (
                !statusResult ||
                statusResult.status === "Error" ||
                statusResult.statusCode >= 400 ||
                statusResult.isSuccess === false ||
                statusResult.success === false
            ) {
                throw new Error(statusResult?.message || statusResult?.Message || "You do not have access to this job site.");
            }

            const statusData = statusResult?.data || statusResult || {};
            setWorkerStatus(statusData);

            const isCheckedIn = Boolean(statusData.isCheckedIn || statusData.IsCheckedIn);

            if (!isCheckedIn) {
                if (locationAccessStatus && locationAccessStatus.canCheckIn === false) {
                    toast.error(locationAccessStatus.reason || "Check-in Blocked: You must be present at the site to check in.");
                    setCheckingIn(false);
                    return;
                }

                setCheckingIn(true);
                let coords = locationAccessStatus?.coords;
                if (!coords || (!coords.latitude && !coords.longitude)) {
                    try {
                        coords = await getCoordinates();
                    } catch (geoErr) {
                        console.warn("Location prompt error on staff login:", geoErr);
                        toast.error("Location permission denied. Please allow location in your browser.");
                        setCheckingIn(false);
                        return;
                    }
                }

                // Fail-fast UX check -- WHS_CheckIn re-validates this authoritatively server-side
                // regardless. Uses the site's real coordinates from jobData, Main and Work alike.
                let hqDistance = "On Site";
                const siteConfig = getJobGeofenceConfig(jobData);
                if (siteConfig && coords) {
                    const geoResult = evaluateGeofence(coords, siteConfig);
                    if (!geoResult.isWithinGeofence) {
                        toast.error(
                            isHqJob
                                ? "Check-in Blocked: You must be present at the site to check in."
                                : "Check-in Blocked: You must be present at the job site to check in."
                        );
                        setCheckingIn(false);
                        return;
                    }
                }

                const res = await checkIn({
                    jobId: jobId,
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    accuracy: coords.accuracy,
                    userId: activeUserId || null,
                    companyId: resolvedCompanyId,
                });

                if (!res || res.status === "Error" || res.statusCode >= 400 || res.isSuccess === false || res.success === false) {
                    throw new Error(res?.message || res?.Message || "Check-in permission denied.");
                }

                await login({
                    username: username,
                    password: password,
                    token: userData.token,
                    userId: userData.userId || userData.UserId,
                    refreshToken: userData.RefreshToken || userData.refreshToken,
                    roles: userData.Roles || userData.roles,
                    userData: userData,
                });

                localStorage.setItem("whs_username", username);
                localStorage.setItem("whs_is_internal_user", "true");
                setUserForm((prev) => ({ ...prev, isInternalUser: true }));

                setCheckInSuccess(true);
                const resData = res?.data || res;
                const checkInTime = resData?.inTime || resData?.InTime || res?.inTime || res?.InTime;
                setActiveCheckInDate(parseDate(checkInTime) || new Date());

                if (isHqJob) {
                    const hqSess = {
                        sessionId: `hq-${Date.now()}`,
                        JobId: MAIN_HQ_CONFIG.id,
                        JobName: MAIN_HQ_CONFIG.name,
                        isMainSite: true,
                        inTime: checkInTime ? new Date(checkInTime).toISOString() : new Date().toISOString(),
                        outTime: null,
                        isCheckedIn: true,
                        distance: hqDistance,
                    };
                    localStorage.setItem("whs_hq_session", JSON.stringify(hqSess));
                }

                const freshStatus = await getWorkerStatus(jobId, activeUserId || null, resolvedCompanyId || null);
                setWorkerStatus(freshStatus?.data || freshStatus);
            } else {
                await login({
                    username: username,
                    password: password,
                    token: userData.token,
                    userId: userData.userId || userData.UserId,
                    refreshToken: userData.RefreshToken || userData.refreshToken,
                    roles: userData.Roles || userData.roles,
                    userData: userData,
                });

                localStorage.setItem("whs_username", username);
                localStorage.setItem("whs_is_internal_user", "true");
                setUserForm((prev) => ({ ...prev, isInternalUser: true }));
                const checkInTime = statusData.inTime || statusData.InTime || statusData.checkInTime || statusData.CheckInTime;
                setActiveCheckInDate(parseDate(checkInTime) || new Date());
            }
        } catch (err) {
            console.error("Login flow error:", err);
            try {
                if (logout) logout();
            } catch (e) {
                console.error("Logout error:", e);
            }
            localStorage.removeItem("authToken");
            localStorage.removeItem("whs_username");
            setSession(null);
            setWorkerStatus(null);
            setActiveCheckInDate(null);
            setLoginError(err.response?.data?.message || err.message || "An error occurred during authentication");
        } finally {
            setLoggingIn(false);
            setCheckingIn(false);
        }
    };

    // Check Out Handler
    const handleCheckOut = async () => {
        try {
            setCheckingOut(true);
            setLoginError(null);

            const jobId = jobData?.jobId || jobData?.JobId;
            const resolvedCompanyId = jobData?.companyId || jobData?.CompanyId || null;
            const resolvedUserId =
                localStorage.getItem("whs_user_id") ||
                userForm?.userId ||
                workerStatus?.userId ||
                workerStatus?.UserId ||
                workerStatus?.data?.userId ||
                workerStatus?.data?.UserId ||
                null;
            // WHS_CheckOut enforces location asymmetrically: the Main site strictly requires it
            // (blocks if missing), a Work site does not (never traps a worker with a bad GPS fix
            // at exit time) -- so a geolocation failure here must NOT abort checkout outright;
            // just proceed without coordinates and let the backend decide.
            let coords = null;
            try {
                coords = await getCoordinates();
            } catch (geoErr) {
                console.warn("Location prompt error during checkout:", geoErr);
            }

            await checkOut({
                jobId: jobId,
                latitude: coords?.latitude,
                longitude: coords?.longitude,
                accuracy: coords?.accuracy,
                exitMethod: "QR",
                userId: resolvedUserId,
                companyId: resolvedCompanyId,
            });

            setFinalCheckOutDate(new Date());
            setCheckOutSuccess(true);
            setActiveCheckInDate(null);
            clearJobSession(token, jobData);
            try {
                const ch = new BroadcastChannel("whs_tab_sync");
                ch.postMessage({ type: "CHECKOUT", token, jobId: jobData?.jobId });
                ch.close();
            } catch (e) {
                // ignore
            }
            lastResolvedToken = null;
            setWorkerStatus(null);
        } catch (err) {
            console.error("Checkout error:", err);
            // loginError isn't rendered during the checkout flow (only on the credentials form),
            // so a rejection here -- e.g. the new "Location is required to sign out of the main
            // site" / "outside the allowed check-out radius" messages from WHS_CheckOut -- must be
            // surfaced via toast or it's silently swallowed from the user's perspective.
            const errMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.message || "An error occurred during checkout";
            toast.error(errMsg);
            setLoginError(errMsg);
        } finally {
            setCheckingOut(false);
            setIsCheckOutModalOpen(false);
        }
    };

    const handleDirectLogout = () => {
        try {
            if (logout) logout();
        } catch (e) {
            console.error("Logout error:", e);
        }
        clearJobSession(token, jobData);
        try {
            const ch = new BroadcastChannel("whs_tab_sync");
            ch.postMessage({ type: "CHECKOUT", token, jobId: jobData?.jobId });
            ch.close();
        } catch (e) {
            // ignore
        }
        localStorage.removeItem("authToken");
        localStorage.removeItem("whs_username");
        localStorage.removeItem("whs_is_internal_user");
        lastResolvedToken = null;
        setSession(null);
        setWorkerStatus(null);
        setActiveCheckInDate(null);
        setFinalCheckOutDate(null);
        setCheckInSuccess(false);
        setCheckOutSuccess(false);
        setUsername("");
        setPassword("");
        resetToScreen1();
    };

    useEffect(() => {
        const handleForceCheckOut = (data) => {
            const currentJobId = (jobData?.jobId || jobData?.JobId || "").toString().toLowerCase();
            const closedJobId = (data?.previousJobId || "").toString().toLowerCase();

            if (!currentJobId || !closedJobId || currentJobId !== closedJobId) return;

            toast.info("You've been signed out — you checked in at a different job site.");
            handleDirectLogout();
        };

        const subId = signalRNotificationService.subscribe("WHS_ForceCheckOut", handleForceCheckOut);
        return () => signalRNotificationService.unsubscribe(subId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobData]);

    const handleDone = () => {
        setCheckOutSuccess(false);
        setCheckInSuccess(false);
        setWorkerStatus(null);
        setActiveCheckInDate(null);
        setFinalCheckOutDate(null);
        clearJobSession(token, jobData);
        try {
            const ch = new BroadcastChannel("whs_tab_sync");
            ch.postMessage({ type: "CHECKOUT", token, jobId: jobData?.jobId });
            ch.close();
        } catch (e) {
            // ignore
        }
        setUsername("");
        setPassword("");
        resetToScreen1();
        window.location.reload();
    };

    const renderContent = () => {
        if (error) {
            return (
                <div className="text-center py-4 font-sans">
                    <div className="text-rose-600 mb-3">
                        <svg className="w-12 h-12 mx-auto animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-base font-bold text-slate-900 mb-1">Verification Failed</h2>
                    <p className="text-slate-500 mb-4 text-xs font-semibold">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full py-2.5 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl shadow-md transition-all cursor-pointer"
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        if (resolving && !jobData) {
            return (
                <div className="text-center py-8 font-sans">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-3 mx-auto"></div>
                    <p className="text-slate-600 text-xs font-bold">Verifying site QR code & location...</p>
                </div>
            );
        }

        const isBlockedFromCheckIn = Boolean(
            locationAccessStatus &&
            locationAccessStatus.canCheckIn === false &&
            !activeCheckInDate &&
            !checkOutSuccess
        );

        if (isBlockedFromCheckIn) {
            return (
                <div className="space-y-4 font-sans animate-fade-in">
                    <div className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-200/90 dark:border-dark-700 shadow-soft text-center">
                        <div className="w-12 h-12 rounded-2xl bg-primary-500/10 text-primary-600 dark:text-primary-400 mx-auto flex items-center justify-center mb-3.5">
                            <MapPinIcon className="w-6 h-6" />
                        </div>

                        <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-1.5">
                            {locationAccessStatus.isPermissionDenied 
                                ? "Location Access Required" 
                                : "Check-In Not Permitted"}
                        </h3>

                        <p className="text-xs text-slate-600 dark:text-dark-300 leading-relaxed max-w-sm mx-auto mb-4 font-medium">
                            {locationAccessStatus.isPermissionDenied 
                                ? "Location permission is required to verify your site presence before displaying site details." 
                                : locationAccessStatus.reason}
                        </p>

                        {locationAccessStatus.isPermissionDenied && (
                            <div className="p-3.5 bg-amber-500/10 dark:bg-amber-950/30 rounded-xl border border-amber-500/20 text-left text-xs mb-4">
                                <div className="flex items-start gap-2.5">
                                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-bold text-amber-900 dark:text-amber-200">
                                            Location is blocked in your browser:
                                        </p>
                                        <ol className="list-decimal list-inside text-[11px] text-amber-800/90 dark:text-amber-300 mt-1 space-y-1">
                                            <li>Look at the permissions popup currently open on the top-left.</li>
                                            <li>Click <strong>"Reset to default"</strong> (or switch <strong>Location to ON</strong>).</li>
                                            <li>Click <strong>"Check Again"</strong> below to continue.</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2.5">
                            <button
                                type="button"
                                onClick={handleRetryLocation}
                                className="w-full py-2.5 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 active:scale-[0.98] rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
                            >
                                <MapPinIcon className="w-4 h-4" />
                                <span>Check Again</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    if (token) {
                                        localStorage.setItem("whs_pending_token", token);
                                    }
                                    navigate("/login", { state: { fromWhsToken: token } });
                                }}
                                className="w-full py-2 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-dark-400 dark:hover:text-white transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                                <LockClosedIcon className="w-3.5 h-3.5" />
                                <span>Internal Staff Sign In</span>
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        if (checkingIn) {
            return (
                <div className="flex flex-col items-center justify-center py-6 text-center font-sans">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-3"></div>
                    <p className="text-xs text-slate-600 font-bold">Registering check-in for job site...</p>
                </div>
            );
        }

        if (checkingOut) {
            return (
                <div className="flex flex-col items-center justify-center py-6 text-center font-sans">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rose-600 mb-3"></div>
                    <p className="text-xs text-slate-600 font-bold">Registering check-out from job site...</p>
                </div>
            );
        }

        if (checkOutSuccess) {
            return (
                <CheckOutSuccessScreen
                    jobTitle={jobData.jobTitle || jobData.displayJobId || jobData.jobId}
                    checkInDate={activeCheckInDate || new Date()}
                    checkOutDate={finalCheckOutDate || new Date()}
                    onDone={handleDone}
                />
            );
        }

        if (activeCheckInDate) {
            return (
                <ActiveSessionCard
                    jobTitle={jobData.jobTitle || jobData.displayJobId || jobData.jobId}
                    checkInDate={activeCheckInDate}
                    onCheckOutClick={() => setIsCheckOutModalOpen(true)}
                    onScanCheckOutClick={() => setIsScanning(true)}
                />
            );
        }

        if (checkInMode === "credentials") {
            return (
                <div className="space-y-4 font-sans">
                    <StaffCredentialsForm
                        jobData={jobData}
                        username={username}
                        setUsername={setUsername}
                        password={password}
                        setPassword={setPassword}
                        loggingIn={loggingIn}
                        loginError={loginError}
                        onSubmit={handleLogin}
                        onSwitchToWizard={() => setCheckInMode("wizard")}
                    />
                </div>
            );
        }

        // Default 3-Step Wizard
        return (
            <div className="space-y-4 font-sans">
                <WhsJobBanner jobData={jobData} headerText={isHqJob ? "Headquarters" : "Job Site"} />

                {wizardStep === 1 && (
                    <Step1UserDetail
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        userForm={userForm}
                        setUserForm={setUserForm}
                        companyType={companyType}
                        setCompanyType={setCompanyType}
                        companyForm={companyForm}
                        setCompanyForm={setCompanyForm}
                        isExistingUser={isExistingUser}
                        loadingLookup={loadingLookup}
                        onLookupPhone={handleLookupPhone}
                        onResetPhone={resetToScreen1}
                        onNext={() => setWizardStep(2)}
                        onSubmit={handleWizardSubmit}
                        isSubmitting={isSubmittingWizard}
                        lookupError={lookupError}
                    />
                )}

                {wizardStep === 2 && (
                    <Step3Certifications
                        certificationsForm={certificationsForm}
                        setCertificationsForm={setCertificationsForm}
                        isExistingUser={isExistingUser}
                        onBack={() => setWizardStep(1)}
                        onSubmit={handleWizardSubmit}
                        isSubmitting={isSubmittingWizard}
                        companyId={jobData?.companyId || jobData?.CompanyId || null}
                    />
                )}

                {wizardStep === 1 && (
                    <div className="pt-3 text-center border-t border-slate-200/60 mt-4">
                        <button
                            type="button"
                            onClick={() => {
                                if (token) {
                                    localStorage.setItem("whs_pending_token", token);
                                }
                                navigate("/login", { state: { fromWhsToken: token } });
                            }}
                            className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-primary-600 transition-all cursor-pointer hover:underline"
                        >
                            <LockClosedIcon className="w-3.5 h-3.5 text-slate-400" />
                            <span>Internal Staff Sign In</span>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const showSideMenu =
        Boolean(isInternalUserSession) &&
        Boolean(activeCheckInDate) &&
        !checkOutSuccess &&
        !checkingOut;

    return (
        <WhsPageLayout
            title="Job Attendance"
            pageHeaderTitle="Job Attendance"
            subtitle="Site Safety & Check-in System"
            currentPage="attendance"
            token={token}
            jobData={jobData}
            userForm={userForm}
            workerStatus={workerStatus}
            activeCheckInDate={activeCheckInDate}
            onCheckOutClick={() => setIsCheckOutModalOpen(true)}
            showSideMenu={showSideMenu}
            maxWidth="sm:max-w-lg"
            modals={
                <>
                    <CheckOutConfirmationModal
                        isOpen={isCheckOutModalOpen}
                        isLoading={checkingOut}
                        onClose={() => setIsCheckOutModalOpen(false)}
                        onConfirm={handleCheckOut}
                        jobTitle={jobData?.jobTitle || jobData?.displayJobId || jobData?.jobId || "Sunny Homes Project"}
                        checkInDate={activeCheckInDate || new Date()}
                    />
                    <QrCameraScannerModal
                        isOpen={isScanning}
                        onClose={() => setIsScanning(false)}
                        videoRef={videoRef}
                        cameraStream={cameraStream}
                    />
                </>
            }
        >
            {renderContent()}
        </WhsPageLayout>
    );
}
